import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  categoryIdToQuerySlug,
  isIndexableVariant,
  normalizeDiscoveryRequest,
  type DiscoveryDescriptor,
  type TerritoryResolution,
  type TerritoryShowcaseItem,
} from '@buzzmap/shared';
import { useDiscoveryOptional } from '../../contexts/discoveryStore';
import { PUBLIC_SITE_ORIGIN } from '../../config/host';

/** Il documento iniziale e il foreground possiedono il head, non lo sfondo della modale. */
interface OwnedBlock {
  node: HTMLScriptElement;
  original: HTMLScriptElement;
  data: Record<string, unknown>;
}

interface InitialDocument {
  identity: string | null;
  coherent: boolean;
  blocks: OwnedBlock[];
  robots: string[];
  /** Pagina di avvio e head così come l'ha consegnata il server (per i documenti non-discovery). */
  pathname: string;
  head: { title: string; canonical: string | null; robots: string | null };
}

/** Stesso titolo dello shell statico index.html: la home generica non deve perderlo. */
const HOME_TITLE = 'Buzz Event Map - Scopri eventi vicino a te';
const ESPLORA_TITLE = 'Esplora eventi | Buzz Event Map';

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function descriptorOf(pathname: string, search: string): DiscoveryDescriptor | null {
  const normalized = normalizeDiscoveryRequest({ pathname, search });
  return normalized.kind === 'canonical' || normalized.kind === 'redirect'
    ? normalized.descriptor
    : null;
}

/** Query di contenuto: stessa forma e ordine di seoCanonicalUrl nel renderer SSR. */
function contentFilterSearch(d: DiscoveryDescriptor): string {
  const parts: string[] = [];
  if (d.categorySource === 'query' && d.categoryIds.length > 0) {
    const slugs = d.categoryIds.map((id) => categoryIdToQuerySlug(id) ?? id);
    parts.push(`categorie=${slugs.join(',')}`);
  }
  if (d.filters.startDate) parts.push(`startDate=${d.filters.startDate}`);
  if (d.filters.endDate && d.filters.endDate !== d.filters.startDate) {
    parts.push(`endDate=${d.filters.endDate}`);
  }
  if (d.filters.radiusKm !== undefined) parts.push(`radiusKm=${d.filters.radiusKm}`);
  if (d.filters.search) parts.push(`search=${encodeURIComponent(d.filters.search)}`);
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

function canonicalPath(d: DiscoveryDescriptor, hasPeriod = false): string {
  const filters = contentFilterSearch(d);
  let path = d.canonicalPath;
  if (filters) {
    path += filters;
  } else if (d.route.kind === 'territory-esplora') {
    path = `/eventi/${d.route.segments.join('/')}`;
  } else if (d.route.kind === 'generic-esplora') {
    path = '/';
  }
  // SSR sceglie prima il canonical della variante; il renderer periodo toglie poi la query.
  return hasPeriod ? (path.split('?')[0] ?? path) : path;
}

/** Tracking e unknown non cambiano i fatti; la variante mappa cambia la marcatura applicabile. */
function metadataIdentity(d: DiscoveryDescriptor): string {
  return JSON.stringify([
    d.canonicalPath,
    contentFilterSearch(d),
    d.periodSlug ?? null,
    d.view,
    d.mapBbox,
  ]);
}

function robotsDirectives(): string[] {
  return Array.from(document.head.querySelectorAll('meta[name="robots"]')).flatMap((meta) =>
    (meta.getAttribute('content') ?? '')
      .split(',')
      .map((part) => part.trim().toLowerCase())
      .filter(Boolean)
  );
}

function compatibleDirectives(directives: string[]): string[] {
  return directives.filter(
    (part) => !['index', 'noindex', 'follow', 'nofollow', 'all', 'none'].includes(part)
  );
}

function setRobots(indexable: boolean, compatible: string[]): void {
  const existing = Array.from(
    document.head.querySelectorAll<HTMLMetaElement>('meta[name="robots"]')
  );
  const robots = existing[0] ?? Object.assign(document.createElement('meta'), { name: 'robots' });
  if (!existing.length) document.head.appendChild(robots);
  existing.slice(1).forEach((meta) => meta.remove());
  const extras = new Map(
    compatibleDirectives(compatible).map((part) => [part.split(':')[0], part])
  );
  robots.content = [indexable ? 'index' : 'noindex', 'follow', ...extras.values()].join(',');
}

function setCanonicalHref(href: string | null): void {
  const links = Array.from(
    document.head.querySelectorAll<HTMLLinkElement>('link[rel="canonical"]')
  );
  if (href === null) {
    links.forEach((link) => link.remove());
    return;
  }
  const link = links[0] ?? Object.assign(document.createElement('link'), { rel: 'canonical' });
  if (!links.length) document.head.appendChild(link);
  links.slice(1).forEach((duplicate) => duplicate.remove());
  link.setAttribute('href', href);
}

function breadcrumbMatchesPage(data: Record<string, unknown>, d: DiscoveryDescriptor): boolean {
  if (!Array.isArray(data.itemListElement)) return false;
  const last: unknown = data.itemListElement[data.itemListElement.length - 1];
  if (!isRecord(last) || typeof last.item !== 'string') return false;
  try {
    const path = new URL(last.item, PUBLIC_SITE_ORIGIN).pathname;
    if (path === d.canonicalPath || path === canonicalPath(d).split('?')[0]) return true;
    // Anche Esplora filtrata usa il breadcrumb del territorio, non della variante.
    return d.route.kind === 'territory-esplora' && path === `/eventi/${d.route.segments.join('/')}`;
  } catch {
    return false;
  }
}

function isDiscoveryBlock(data: Record<string, unknown>): boolean {
  if (data['@graph'] !== undefined || !Array.isArray(data.itemListElement)) return false;
  if (data['@type'] === 'BreadcrumbList') {
    const last: unknown = data.itemListElement[data.itemListElement.length - 1];
    if (!isRecord(last) || typeof last.item !== 'string') return false;
    try {
      const url = new URL(last.item, PUBLIC_SITE_ORIGIN);
      return (
        [PUBLIC_SITE_ORIGIN, window.location.origin].includes(url.origin) &&
        url.pathname.startsWith('/eventi/')
      );
    } catch {
      return false;
    }
  }
  if (data['@type'] !== 'ItemList' || data.itemListElement.length === 0) return false;
  return data.itemListElement.every((entry: unknown) => {
    if (!isRecord(entry)) return false;
    const item = isRecord(entry.item) ? entry.item : entry;
    if (typeof item.url !== 'string' || typeof item.name !== 'string') return false;
    if (isRecord(entry.item) && item['@type'] !== 'Event') return false;
    try {
      const url = new URL(item.url, PUBLIC_SITE_ORIGIN);
      return (
        [PUBLIC_SITE_ORIGIN, window.location.origin].includes(url.origin) &&
        /^\/event\/[^/]+$/.test(url.pathname)
      );
    } catch {
      return false;
    }
  });
}

function captureInitialDocument(): InitialDocument {
  const normalized = normalizeDiscoveryRequest({
    pathname: window.location.pathname,
    search: window.location.search,
  });
  const d =
    normalized.kind === 'canonical' || normalized.kind === 'redirect'
      ? normalized.descriptor
      : null;
  const discoveryDocument = !(
    normalized.kind === 'invalid-route' && normalized.reason === 'not-a-discovery-path'
  );
  const blocks: OwnedBlock[] = [];
  if (discoveryDocument) {
    for (const node of document.head.querySelectorAll<HTMLScriptElement>(
      'script[type="application/ld+json"]'
    )) {
      try {
        const data: unknown = JSON.parse(node.textContent ?? '');
        // I renderer emettono blocchi separati. Event, Organization, grafi misti
        // e JSON non riconoscibile non sono proprietà di questo componente.
        if (isRecord(data) && isDiscoveryBlock(data)) {
          blocks.push({ node, original: node.cloneNode(true) as HTMLScriptElement, data });
        }
      } catch {
        // Non cancellare dati di cui non è possibile stabilire la proprietà.
      }
    }
  }
  const href = document.head
    .querySelector<HTMLLinkElement>('link[rel="canonical"]')
    ?.getAttribute('href');
  let coherent = false;
  if (d && href) {
    try {
      const canonical = new URL(href, window.location.origin);
      const path = `${canonical.pathname}${canonical.search}`;
      const sameOrigin = [PUBLIC_SITE_ORIGIN, window.location.origin].includes(canonical.origin);
      coherent =
        sameOrigin &&
        (path === canonicalPath(d) ||
          (d.periodSlug !== undefined && path === canonicalPath(d, true)));
      coherent &&= blocks.every(
        ({ data }) => data['@type'] !== 'BreadcrumbList' || breadcrumbMatchesPage(data, d)
      );
    } catch {
      coherent = false;
    }
  }
  return {
    identity: d ? metadataIdentity(d) : null,
    coherent,
    blocks,
    robots: robotsDirectives(),
    pathname: window.location.pathname,
    head: {
      title: document.title,
      canonical: href ?? null,
      robots:
        document.head.querySelector('meta[name="robots"]')?.getAttribute('content') ?? null,
    },
  };
}

/** Ripristina il head consegnato dal server dopo che una rotta discovery lo ha riscritto. */
function restoreBootHead(initial: InitialDocument): void {
  document.title = initial.head.title;
  setCanonicalHref(initial.head.canonical);
  document.head.querySelectorAll('meta[name="robots"]').forEach((meta) => meta.remove());
  if (initial.head.robots !== null) {
    const meta = Object.assign(document.createElement('meta'), {
      name: 'robots',
      content: initial.head.robots,
    });
    document.head.appendChild(meta);
  }
}

function removeOwnedBlocks(initial: InitialDocument): void {
  for (const block of initial.blocks) {
    // Un altro owner può avere riutilizzato il nodo: non rimuoverne il nuovo contenuto.
    if (block.node.textContent === block.original.textContent) block.node.remove();
  }
}

function restoreOwnedBlocks(initial: InitialDocument): void {
  for (const block of initial.blocks) {
    if (block.node.isConnected) continue;
    block.node = block.original.cloneNode(true) as HTMLScriptElement;
    document.head.appendChild(block.node);
  }
}

function eventMatches(item: Record<string, unknown>, event: TerritoryShowcaseItem): boolean {
  if (typeof item.url !== 'string' || item.name !== event.title) return false;
  try {
    if (new URL(item.url, PUBLIC_SITE_ORIGIN).pathname !== `/event/${event.id}`) return false;
  } catch {
    return false;
  }
  if (
    item.startDate !== undefined &&
    (typeof item.startDate !== 'string' || item.startDate.slice(0, 10) !== event.startDate)
  )
    return false;
  if (
    item.endDate !== undefined &&
    (typeof item.endDate !== 'string' ||
      item.endDate.slice(0, 10) !== (event.endDate ?? event.startDate))
  )
    return false;
  if (item.image !== undefined && item.image !== event.imageUrl) return false;
  if (isRecord(item.location)) {
    if (isRecord(item.location.address) && item.location.address.addressLocality !== event.city)
      return false;
    if (item.location.name !== undefined && item.location.name !== (event.venueName || event.city))
      return false;
  }
  return true;
}

/** Per il ripristino servono fatti verificabili, non soltanto gli stessi UUID. */
function showcaseMatches(
  initial: InitialDocument,
  resolution: TerritoryResolution,
  restoring: boolean
): boolean {
  return initial.blocks.every(({ data }) => {
    if (data['@type'] !== 'ItemList') return true;
    if (!Array.isArray(data.itemListElement) || !Array.isArray(resolution.showcase)) return false;
    if (data.itemListElement.length !== resolution.showcase.length) return false;
    return data.itemListElement.every((entry: unknown, index: number) => {
      const event = resolution.showcase[index];
      if (!isRecord(entry) || !event || entry.position !== index + 1) return false;
      const item = isRecord(entry.item) ? entry.item : entry;
      if (!eventMatches(item, event)) return false;
      if (!restoring) return true;
      if (!isRecord(entry.item)) {
        return Object.keys(entry).every((key) =>
          ['@type', 'position', 'url', 'name'].includes(key)
        );
      }
      if (
        [item.startDate, item.endDate].some(
          (date) => typeof date === 'string' && date.length !== 10
        )
      )
        return false;
      if (item.location !== undefined && !isRecord(item.location)) return false;
      // Il DTO vetrina non certifica prezzo, CAP, coordinate o altri dettagli
      // Event: la marcatura completa iniziale non viene ricreata né riattivata alla cieca.
      const known = ['@type', 'url', 'name', 'startDate', 'endDate', 'image', 'location'];
      if (Object.keys(item).some((key) => !known.includes(key))) return false;
      if (isRecord(item.location)) {
        if (Object.keys(item.location).some((key) => !['@type', 'name', 'address'].includes(key)))
          return false;
        if (
          isRecord(item.location.address) &&
          Object.keys(item.location.address).some(
            (key) => !['@type', 'addressLocality'].includes(key)
          )
        )
          return false;
      }
      return true;
    });
  });
}

export function RouteMetadata() {
  const discovery = useDiscoveryOptional();
  const raw = useLocation();
  const initialRef = useRef<InitialDocument | null>(null);
  if (initialRef.current === null) initialRef.current = captureInitialDocument();
  const displacedRef = useRef(false);
  const writtenIdentityRef = useRef<string | null>(null);

  const mode = discovery?.mode;
  const descriptor = discovery?.descriptor;
  const resolution = discovery?.resolution;
  const status = discovery?.status;
  const settled = discovery?.settled;
  const pending = discovery?.pending;
  const pathname = raw.pathname;
  const search = raw.search;

  useEffect(() => {
    if (mode !== 'v2') return;
    const initial = initialRef.current!;
    const foregroundOwned =
      pathname.startsWith('/event/') ||
      ['/accedi', '/registrati', '/password-reset', '/password-update', '/auth/callback'].includes(
        pathname
      );
    if (foregroundOwned) {
      removeOwnedBlocks(initial);
      displacedRef.current = true;
      writtenIdentityRef.current = null;
      return;
    }

    const foreground = descriptorOf(pathname, search);

    // Rotte statiche (/privacy, /termini, /cookie-policy, ...): non sono rotte discovery, quindi
    // non esiste nulla da risolvere e il fallback di errore non si applica. Sul documento di
    // avvio il head resta quello del server (nessun noindex forzato, nessun titolo generico);
    // se una rotta discovery l'aveva riscritto, lo si ripristina. Solo dopo una navigazione SPA
    // da un altro documento si toglie l'identità precedente (canonical/JSON-LD), perché
    // descriverebbe un'altra pagina.
    if (foreground === null && status !== 'invalid') {
      const normalized = normalizeDiscoveryRequest({ pathname, search });
      const staticRoute =
        normalized.kind === 'invalid-route' && normalized.reason === 'not-a-discovery-path';
      if (staticRoute && pathname === initial.pathname) {
        if (writtenIdentityRef.current !== null || displacedRef.current) {
          restoreBootHead(initial);
          writtenIdentityRef.current = null;
          displacedRef.current = false;
        }
        return;
      }
    }

    const identity = foreground ? metadataIdentity(foreground) : null;
    const sameInitial = identity !== null && identity === initial.identity && initial.coherent;
    const pertinent = descriptor && foreground && metadataIdentity(descriptor) === identity;
    const ready = pertinent && resolution && status === 'ready' && settled && !pending;
    if (!sameInitial) {
      removeOwnedBlocks(initial);
      displacedRef.current = true;
    }

    // Sul primo documento valido le API lente o indisponibili non eliminano
    // l'unico contenuto autorevole. Dopo una navigazione serve una nuova prova.
    if (!ready && sameInitial && !displacedRef.current && status !== 'invalid') {
      // L'assenza di noindex significa index: SSR non emette index/follow espliciti.
      const initialIndexable = !initial.robots.some(
        (part) => part === 'noindex' || part === 'none'
      );
      const indexable = initialIndexable && foreground !== null && isIndexableVariant(foreground);
      const current = robotsDirectives();
      const currentIndexable = !current.some((part) => part === 'noindex' || part === 'none');
      const currentFollow = !current.some((part) => part === 'nofollow' || part === 'none');
      if (
        currentIndexable !== indexable ||
        !currentFollow ||
        current.includes('all') ||
        current.includes('none') ||
        (current.includes('index') && current.includes('noindex'))
      ) {
        setRobots(indexable, initial.robots);
      }
      writtenIdentityRef.current = identity;
      return;
    }

    // Home e Esplora generiche non hanno territorio: lo stato discovery resta 'idle' e non
    // arriverà mai a 'ready'. Il head dipende solo dall'URL, quindi si scrive qui (non è un errore).
    if (
      foreground &&
      (foreground.route.kind === 'generic-home' || foreground.route.kind === 'generic-esplora') &&
      status !== 'invalid' &&
      status !== 'unavailable'
    ) {
      const home = foreground.route.kind === 'generic-home';
      removeOwnedBlocks(initial);
      displacedRef.current = true;
      document.title = home ? HOME_TITLE : ESPLORA_TITLE;
      setCanonicalHref(`${PUBLIC_SITE_ORIGIN}${canonicalPath(foreground)}`);
      setRobots(isIndexableVariant(foreground), []);
      writtenIdentityRef.current = identity;
      return;
    }

    let compatible: string[];
    if (sameInitial && writtenIdentityRef.current === identity) {
      compatible = [...initial.robots, ...robotsDirectives()];
    } else if (sameInitial) {
      compatible = initial.robots;
    } else if (writtenIdentityRef.current === identity && identity !== null) {
      compatible = robotsDirectives();
    } else {
      compatible = initial.robots.filter((part) => part === 'max-image-preview:large');
    }

    if (ready) {
      const isCategory = descriptor.route.kind === 'territory-category';
      if (resolution.period) {
        document.title = `${resolution.heading} (${resolution.period.rangeLabel}) | Buzz Event Map`;
      } else if (isCategory) {
        document.title = `${resolution.heading} — prossime date | Buzz Event Map`;
      } else {
        document.title = `${resolution.heading} | Buzz Event Map`;
      }
      setCanonicalHref(
        `${PUBLIC_SITE_ORIGIN}${canonicalPath(descriptor, resolution.period !== undefined)}`
      );
      setRobots(resolution.indexable && isIndexableVariant(descriptor), compatible);
      if (sameInitial && showcaseMatches(initial, resolution, displacedRef.current)) {
        restoreOwnedBlocks(initial);
      } else {
        removeOwnedBlocks(initial);
        displacedRef.current = true;
      }
      writtenIdentityRef.current = identity;
      return;
    }

    removeOwnedBlocks(initial);
    setCanonicalHref(null);
    setRobots(false, compatible);
    if (status === 'invalid') {
      document.title = 'Filtri non validi | Buzz Event Map';
    } else if (status === 'unavailable') {
      document.title = 'Pagina non trovata | Buzz Event Map';
    } else {
      document.title = 'Buzz Event Map';
    }
    writtenIdentityRef.current = identity;
  }, [mode, descriptor, resolution, status, settled, pending, pathname, search]);

  return null;
}
